// Web accent registry — the SINGLE source of truth for the browser's per-user
// accent themes (Settings ▸ Appearance ▸ Accent).
//
// WHY THIS IS SEPARATE FROM theme-definitions.ts. Those 9 themes are the
// TERMINAL palette (ANSI/xterm codes, tuned for a text console). The web app is
// its own surface: an accent here is a `--primary` FILL sitting behind a label,
// plus a focus ring — it needs light/dark values and a readable foreground per
// mode, which the terminal palette doesn't carry. Keeping the two registries
// apart lets each be tuned for its medium without one dragging the other.
//
// HOW IT IS APPLIED. Never by writing `--primary` from JS (that was the #549
// regression the theme-token guard now forbids). Each accent maps to a scoped
// CSS class `.<name>-<light|dark>` whose rule — generated into globals.css by
// `pnpm gen:accent-css` and verified by `pnpm check-accent-css-lockstep` — sets
// `--primary`, `--primary-foreground`, and `--ring`. The document root carries
// that class (composed server-side in app/layout.tsx, kept in sync at runtime by
// theme-provider.tsx), so the stylesheet — not JS — owns the token.
//
// `indigo` is the brand default; an unset or unrecognised accent resolves to it.

import data from "./theme-accents.json";

export interface AccentTone {
  /** `--primary` fill for this mode. */
  primary: string;
  /** `--ring` focus colour (tracks the brand hue). */
  ring: string;
  /** `--primary-foreground` label colour, chosen for contrast on `primary`. */
  fg: string;
}

export interface WebAccent {
  /** Stable key persisted as `appearance.accent` and used in the CSS class. */
  name: string;
  /** Human label for the picker. */
  label: string;
  light: AccentTone;
  dark: AccentTone;
}

export const WEB_ACCENTS: WebAccent[] = data.accents as WebAccent[];
export const DEFAULT_WEB_ACCENT: string = data.default;
export const WEB_ACCENT_NAMES: string[] = WEB_ACCENTS.map((a) => a.name);

export function webAccentByName(name?: string | null): WebAccent | undefined {
  if (!name) return undefined;
  return WEB_ACCENTS.find((a) => a.name === name.trim().toLowerCase());
}

export function isWebAccent(name: string): boolean {
  return WEB_ACCENT_NAMES.includes(name);
}

/** Resolve any input to a valid accent name, falling back to the brand default. */
export function normalizeWebAccent(name?: string | null): string {
  return name && isWebAccent(name) ? name : DEFAULT_WEB_ACCENT;
}

/** The swatch colour to show in the picker (the dark-mode fill reads well on
 *  either canvas). Falls back to the brand indigo. */
export function accentSwatch(name: string): string {
  const a = webAccentByName(name) ?? webAccentByName(DEFAULT_WEB_ACCENT);
  return a ? a.dark.primary : "#6366F1";
}
